// src/modules/goldInventory/pages/InventoryDashboard.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchBatches, createBatch, getVaultSummary, deleteBatch } from '../api';
import { fetchWalkInCases } from '../../walkInCase/api';
import { fetchPledgeReleaseCases } from '../../pledgeReleaseCase/api';
import { getHOVault } from '../../goldVault/api';
import {
  Table, Button, Typography, Spin, Tag, Row, Col, Card,
  Modal, Select, message, Statistic, Space, Popconfirm, Form, InputNumber, Input, Radio, Divider, Alert,
} from 'antd';
import { PlusOutlined, ReloadOutlined, MinusCircleOutlined, PlusCircleOutlined, SettingOutlined } from '@ant-design/icons';
import { usePermissions } from '../../../hooks/usePermissions';
import GroupedMeltSection, { assembleGroupedItems } from '../components/GroupedMeltSection';

const { Title } = Typography;

const STATUS_COLORS = { HOLDING: 'gold', DISPATCHED: 'orange', SETTLED: 'green' };
const fmt = (v) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);

export default function InventoryDashboard() {
  const navigate = useNavigate();
  const { canViewCreateModule, canEditModule, can, canViewField, role } = usePermissions();
  const isAdmin = role === 'admin';

  const canViewBatchId = canViewField('goldInventory', 'batchId');
  const canViewNetWeight = canViewField('goldInventory', 'totalNetWeight');
  const canViewGrossWeight = canViewField('goldInventory', 'totalGrossWeight');
  const canViewPurchaseCost = canViewField('goldInventory', 'totalPurchaseCost');
  const canViewStatusField = canViewField('goldInventory', 'status');
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [vault, setVault] = useState(null);
  const [hoVault, setHoVault] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [statusFilter, setStatusFilter] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [eligibleCases, setEligibleCases] = useState([]);
  const [selectedCaseIds, setSelectedCaseIds] = useState([]);
  const [caseDetailMap, setCaseDetailMap] = useState({});
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();
  const [form] = Form.useForm();
  const batchType = Form.useWatch('batchType', form);
  // GROUPED batch working state (source items + melt groups)
  const [groupedSource, setGroupedSource] = useState([]);
  const [groupedGroups, setGroupedGroups] = useState([]);
  // Weight of the selected cases as the HO vault holds it — this whole amount leaves
  // the vault on create, whatever the melt yields.
  const [sourceTotals, setSourceTotals] = useState({ gross: 0, net: 0 });

  useEffect(() => { load(); loadVault(); }, [pagination.current, pagination.pageSize, statusFilter]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetchBatches({ page: pagination.current, limit: pagination.pageSize, status: statusFilter });
      setBatches(res.data || []);
      console.log('Fetched batches:', res.data);
      setPagination((p) => ({ ...p, total: res.meta?.total || 0 }));
    } catch { messageApi.error('Failed to load batches'); }
    finally { setLoading(false); }
  };

  const loadVault = async () => {
    try {
      const [summaryRes, hoRes] = await Promise.allSettled([getVaultSummary(), getHOVault()]);
      if (summaryRes.status === 'fulfilled') setVault(summaryRes.value.data);
      if (hoRes.status === 'fulfilled') setHoVault(hoRes.value.data);
    } catch { /* non-critical */ }
  };

  const openCreateModal = async () => {
    try {
      const [walkRes, pledgeRes] = await Promise.all([
        fetchWalkInCases({ inventoryStatus: 'IN_HO_VAULT', limit: 200 }),
        fetchPledgeReleaseCases({ inventoryStatus: 'IN_HO_VAULT', limit: 200, includeGoldReceipt: true }),
      ]);

      const detailMap = {};
      const walkOptions = (walkRes.data || []).map((c) => {
        const value = `walk:${c._id}`;
        detailMap[value] = { type: 'walk', data: c };
        return {
          value,
          id: c._id,
          type: 'walk',
          label: `[Walk-In] ${c.caseId} — ${c.customerId?.name || 'Unknown'} (${(c.totalNetWeight || 0).toFixed(3)}g)`
        };
      });
      const pledgeOptions = (pledgeRes.data || []).map((c) => {
        const value = `pledge:${c._id}`;
        const detail = { type: 'pledge', data: c };
        detailMap[value] = detail;
        // Label the assayed net weight, not the pre-valuation estimate.
        const { net } = caseSourceWeights(detail);
        return {
          value,
          id: c._id,
          type: 'pledge',
          label: `[Pledge] ${c.caseId} — ${c.customerId?.name || 'Unknown'} (${net.toFixed(3)}g)`
        };
      });

      setEligibleCases([...walkOptions, ...pledgeOptions]);
      setCaseDetailMap(detailMap);
    } catch { messageApi.error('Failed to load eligible cases'); }
    form.setFieldsValue({ batchType: 'SINGLE', items: [{ label: 'Item 1' }] });
    setGroupedSource([]); setGroupedGroups([]);
    setCreateModalOpen(true);
  };

  // What the company actually paid out to acquire a case's gold. This is the amount
  // debited to 1200 Gold Inventory at bill time, so batch item prices must sum to it.
  //   Walk-In: finalPaymentToCustomer = totalPurchaseAmount − processingFee + rounding
  //   Pledge:  finalNetValue          = finalGoldValue     − processingFee + rounding
  // The per-line totalPrice figures are gross (pre-fee, pre-rounding) and must never
  // be used directly — they under/over-state the batch cost.
  const caseFinalAmount = (detail) => {
    const c = detail?.data || {};
    if (detail?.type === 'walk') {
      return Number(c.finalPaymentToCustomer) || Number(c.totalPurchaseAmount) || 0;
    }
    return (
      Number(c.finalNetValue) ||
      Number(c.goldReceipt?.finalNetValue) ||
      Number(c.approxFinalAmount) ||
      Number(c.finalGoldValue) ||
      0
    );
  };

  // The weights that actually entered the vault, and so exactly what the backend's
  // caseRefs will debit out of it. A pledge case's own totalGross/NetWeight are only
  // the pre-valuation estimate taken when the case was opened; the assayed GoldReceipt
  // weights are what the customer was paid on and what the branch vault was credited
  // with. Read the estimate on one side and the assay on the other and the melt
  // reconciliation shows a phantom gain.
  const caseSourceWeights = (detail) => {
    const c = detail?.data || {};
    const r = detail?.type === 'pledge' ? c.goldReceipt : null;
    const net = Number(r?.finalTotalNetWeight)
      || Number(c.finalTotalNetWeight)
      || Number(c.totalNetWeight)
      || 0;
    const gross = Number(r?.finalTotalGrossWeight)
      || Number(c.finalTotalGrossWeight)
      || Number(c.totalGrossWeight)
      || net;
    return { gross, net };
  };

  // Spread a case's final payout across its line items pro-rata by line value. The last
  // item absorbs the rounding residual so the parts sum to exactly `total`.
  const allocatePrices = (lineValues, total) => {
    const count = lineValues.length;
    if (!count) return [];
    const round2 = (v) => Math.round(v * 100) / 100;
    if (!total) return lineValues.map(() => 0);

    const sum = lineValues.reduce((s, v) => s + v, 0);
    // No line values to weight by (e.g. all zero) — split evenly.
    const parts = sum > 0
      ? lineValues.map((v) => round2((v * total) / sum))
      : lineValues.map(() => round2(total / count));

    const residual = round2(total - parts.reduce((s, p) => s + p, 0));
    parts[count - 1] = round2(parts[count - 1] + residual);
    return parts;
  };

  const buildItemsFromSelection = (ids) => {
    const items = [];
    ids.forEach((value) => {
      const detail = caseDetailMap[value];
      const c = detail?.data || {};
      const prefix = detail?.type === 'walk' ? 'Walk-In' : 'Pledge';
      const caseLabel = c.caseId || value;
      const finalAmount = caseFinalAmount(detail);

      const lines = detail?.type === 'walk'
        ? (c.goldItems || [])
        : (c.goldReceipt?.finalGoldItems || []);

      if (lines.length === 0) {
        // Same resolution as selectedCaseTotals — never the raw pledge estimate.
        const { gross, net } = caseSourceWeights(detail);
        const purityPercent = gross ? Number(((net / gross) * 100).toFixed(2)) : 0;
        items.push({
          label: `${prefix} ${caseLabel}`,
          grossWeight: gross,
          purityPercent,
          priceAmount: finalAmount,
        });
        return;
      }

      const prices = allocatePrices(lines.map((l) => Number(l.totalPrice) || 0), finalAmount);
      lines.forEach((item, idx) => {
        items.push({
          label: item.itemName || `${prefix} ${caseLabel} Item ${idx + 1}`,
          grossWeight: Number(item.grossWeight) || 0,
          purityPercent: Number(item.purity) || 0,
          priceAmount: prices[idx],
        });
      });
    });
    return items;
  };

  const handleCreateBatch = async () => {
    if (!selectedCaseIds.length) return messageApi.warning('Select at least one case');
    const values = await form.validateFields();
    const { batchType, items = [] } = values;
    const walkInCaseIds = selectedCaseIds.filter((v) => v.startsWith('walk:')).map((v) => v.split(':')[1]);
    const pledgeCaseIds = selectedCaseIds.filter((v) => v.startsWith('pledge:')).map((v) => v.split(':')[1]);

    if (batchType === 'GROUPED') {
      const { items: groupedItems, error } = assembleGroupedItems(groupedSource, groupedGroups);
      if (error) return messageApi.warning(error);
      values.items = groupedItems;
    }

    setCreating(true);
    try {
      if (batchType === 'SINGLE') {
        values.totalNetWeight = Number((((values.totalGrossWeight || 0) * (values.purityPercent || 0)) / 100).toFixed(4));
      }
      await createBatch({ walkInCaseIds, pledgeCaseIds, ...values });
      messageApi.success('Inventory batch created');
      setCreateModalOpen(false);
      setSelectedCaseIds([]);
      setGroupedSource([]); setGroupedGroups([]);
      form.resetFields();
      load(); loadVault();
    } catch (e) {
      messageApi.error(e.response?.data?.message || 'Failed to create batch');
    } finally { setCreating(false); }
  };

  const selectedCaseTotals = (ids) => ids.reduce((acc, value) => {
    const { gross, net } = caseSourceWeights(caseDetailMap[value]);
    acc.gross += gross;
    acc.net += net;
    return acc;
  }, { gross: 0, net: 0 });

  useEffect(() => {
    if (!selectedCaseIds.length) { setSourceTotals({ gross: 0, net: 0 }); return; }
    const totals = selectedCaseTotals(selectedCaseIds);
    setSourceTotals({
      gross: Number(totals.gross.toFixed(3)),
      net: Number(totals.net.toFixed(3)),
    });
  }, [selectedCaseIds, caseDetailMap]);

  useEffect(() => {
    if (selectedCaseIds.length) {
      const items = buildItemsFromSelection(selectedCaseIds);
      if (batchType === 'GROUPED') {
        // Load source items for grouping, preserving prior group assignments where possible.
        setGroupedSource((prev) => {
          const prevByLabel = new Map(prev.map((p) => [p.label, p.groupId]));
          return items.map((it, idx) => ({
            key: `si-${idx}-${it.label}`,
            label: it.label,
            grossWeight: Number(it.grossWeight) || 0,
            purityPercent: Number(it.purityPercent) || 0,
            priceAmount: Number(it.priceAmount) || 0,
            groupId: prevByLabel.get(it.label) || null,
          }));
        });
      } else if (batchType === 'ITEMIZED') {
        if (items.length) form.setFieldsValue({ items });
      } else {
        const sourceGross = items.reduce((s, i) => s + (i.grossWeight || 0), 0);
        const sourceNet = items.reduce((s, i) => s + ((i.grossWeight * i.purityPercent) / 100), 0);
        const sourcePrice = items.reduce((s, i) => s + (i.priceAmount || 0), 0);
        const purity = sourceGross ? (sourceNet / sourceGross) * 100 : 0;
        
        form.setFieldsValue({
          sourceTotalGrossWeight: Number(sourceGross.toFixed(3)),
          sourceTotalPurchaseCost: Number(sourcePrice.toFixed(2)),
          totalGrossWeight: Number(sourceGross.toFixed(3)),
          meltingLoss: 0,
          purityPercent: Number(purity.toFixed(2)),
        });
      }
    }
  }, [batchType, selectedCaseIds, caseDetailMap]);

  const handleCaseChange = (values) => {
    setSelectedCaseIds(values);
  };

  const columns = [
    { title: 'Batch ID', dataIndex: 'batchId', key: 'batchId', width: 140,
      render: (v, r) => <Button type="link" onClick={() => navigate(`/gold-inventory/${r._id}`)}>{v}</Button> },
    canViewStatusField && { title: 'Status', dataIndex: 'status', key: 'status', width: 120,
      render: (v) => <Tag color={STATUS_COLORS[v]}>{v}</Tag> },
    { title: 'Cases/Items', key: 'count', width: 100,
      render: (_, r) => (['ITEMIZED', 'GROUPED'].includes(r.batchType)
        ? `${r.itemCount || 0} items`
        : `${r.caseCount || 0} cases`) },
    canViewNetWeight && { title: 'Net Wt (g)', dataIndex: 'totalNetWeight', key: 'totalNetWeight', width: 110,
      render: (v) => v?.toFixed(3) },
    canViewGrossWeight && { title: 'Gross Wt (g)', dataIndex: 'totalGrossWeight', key: 'totalGrossWeight', width: 120,
      render: (v) => v?.toFixed(3) },
    canViewPurchaseCost && { title: 'Purchase Cost', dataIndex: 'totalPurchaseCost', key: 'totalPurchaseCost', width: 130,
      render: (v) => fmt(v) },
    { title: 'Branch', key: 'branch', width: 120,
      render: (_, r) => r.branch?.name || '—' },
    { title: 'Created', dataIndex: 'createdAt', key: 'createdAt', width: 110,
      render: (v) => v ? new Date(v).toLocaleDateString('en-IN') : '—' },
    { title: 'Actions', key: 'actions', fixed: 'right', width: 180,
      render: (_, r) => (
        <Space>
          {r.status === 'HOLDING' && (
            <Button type="primary" size="small" onClick={() => navigate(`/smelters/dispatches/create?batchId=${r._id}`)}>
              Dispatch
            </Button>
          )}
          {r.smelterDispatchId && (
            <Button size="small" onClick={() => navigate(`/smelters/dispatches/${r.smelterDispatchId._id || r.smelterDispatchId}`)}>
              View Dispatch
            </Button>
          )}
          {can('goldInventory', 'delete') && (isAdmin || (r.status === 'HOLDING' && !r.smelterDispatchId)) && (
            <Popconfirm
              title="Delete this batch?"
              description={isAdmin && (r.status !== 'HOLDING' || r.smelterDispatchId)
                ? 'Admin cascade: linked dispatch and profit records will also be deleted.'
                : 'Cases will move back to inventory.'}
              okText="Delete"
              okButtonProps={{ danger: true, loading: deletingId === r._id }}
              onConfirm={() => handleDelete(r._id)}
            >
              <Button
                danger
                size="small"
                loading={deletingId === r._id}
              >
                Delete
              </Button>
            </Popconfirm>
          )}
        </Space>
      ) },
  ].filter(Boolean);

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await deleteBatch(id);
      messageApi.success('Batch deleted');
      await Promise.all([load(), loadVault()]);
    } catch (e) {
      messageApi.error(e.response?.data?.message || 'Failed to delete batch');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      {contextHolder}

      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col><Title level={4} style={{ margin: 0 }}>Gold Inventory</Title></Col>
        <Col>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => { load(); loadVault(); }}>Refresh</Button>
            {canEditModule('goldInventory') && (
              <Button icon={<SettingOutlined />} onClick={() => navigate('/gold-inventory/invoice-settings')}>
                Company / Invoice Details
              </Button>
            )}
            {canViewCreateModule('goldInventory') && (
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>Create Batch</Button>
            )}
          </Space>
        </Col>
      </Row>

      {/* HO Vault live balance — gold available to batch */}
      {hoVault && (
        <Row gutter={[16, 16]} style={{ marginBottom: 8 }}>
          <Col span={24}>
            <Card
              size="small"
              bordered
              style={{ background: '#f6ffed', borderColor: '#b7eb8f' }}
              bodyStyle={{ padding: '12px 16px' }}
            >
              <Row align="middle" gutter={32}>
                <Col>
                  <Typography.Text strong style={{ color: '#237804', fontSize: 13 }}>
                    HO Gold Vault — Available to Batch
                  </Typography.Text>
                </Col>
                <Col>
                  <Typography.Text style={{ color: '#237804' }}>
                    Gross: <strong>{Number(hoVault.currentGrossWeight || 0).toFixed(3)}g</strong>
                  </Typography.Text>
                </Col>
                {canViewNetWeight && (
                  <Col>
                    <Typography.Text style={{ color: '#237804' }}>
                      Net: <strong>{Number(hoVault.currentNetWeight || 0).toFixed(3)}g</strong>
                    </Typography.Text>
                  </Col>
                )}
                <Col>
                  <Typography.Text style={{ color: '#237804' }}>
                    Items: <strong>{hoVault.totalItems || 0}</strong>
                  </Typography.Text>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      )}

      {/* Batch summary stats */}
      {vault && (
        <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
          {[
            { title: 'Holding Batches', value: vault.batchCount || 0, suffix: 'batches' },
            { title: 'Cases In Batches', value: vault.caseCount || 0, suffix: 'cases' },
            canViewNetWeight && { title: 'Batched Net Wt', value: `${(vault.totalNetWeight || 0).toFixed(3)}g` },
            canViewPurchaseCost && { title: 'Batched Purchase Cost', value: fmt(vault.totalPurchaseCost) },
          ].filter(Boolean).map((s) => (
            <Col xs={12} sm={6} key={s.title}>
              <Card size="small" bordered>
                <Statistic title={s.title} value={s.value} valueStyle={{ fontSize: 20, fontWeight: 600 }} />
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Row style={{ marginBottom: 12 }}>
        <Col>
          <Select
            placeholder="All Statuses" allowClear style={{ width: 180 }}
            value={statusFilter || undefined}
            onChange={(v) => { setPagination((p) => ({ ...p, current: 1 })); setStatusFilter(v || ''); }}
          >
            {['HOLDING', 'DISPATCHED', 'SETTLED'].map((s) => (
              <Select.Option key={s} value={s}>{s}</Select.Option>
            ))}
          </Select>
        </Col>
      </Row>

      <Spin spinning={loading}>
        <Table
          dataSource={batches} rowKey="_id" columns={columns}
          scroll={{ x: 1000 }} size="small"
          pagination={{
            current: pagination.current, pageSize: pagination.pageSize, total: pagination.total,
            showSizeChanger: true,
            onChange: (page, size) => setPagination({ current: page, pageSize: size, total: pagination.total }),
          }}
        />
      </Spin>

      {/* Create batch modal */}
      <Modal
        title="Create Inventory Batch"
        open={createModalOpen}
        onCancel={() => { setCreateModalOpen(false); setSelectedCaseIds([]); setGroupedSource([]); setGroupedGroups([]); form.resetFields(); }}
        onOk={handleCreateBatch}
        okText="Create Batch"
        confirmLoading={creating}
        width={760}
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Batch Type" name="batchType" initialValue="SINGLE">
            <Radio.Group>
              <Radio value="SINGLE">Single (melt all into one)</Radio>
              <Radio value="ITEMIZED">Itemized (each item separate)</Radio>
              <Radio value="GROUPED">Grouped Melt (merge some, keep others separate)</Radio>
            </Radio.Group>
          </Form.Item>

          <p style={{ marginBottom: 8 }}>Select cases currently in <strong>HO Gold Vault</strong> (<strong>IN_HO_VAULT</strong>) to add to this batch. Only gold received at HO after a completed transfer is eligible:</p>
          <Select
            mode="multiple"
            style={{ width: '100%', marginBottom: 16 }}
            placeholder="Select cases…"
            value={selectedCaseIds}
            onChange={handleCaseChange}
            filterOption={(input, opt) => opt.label?.toLowerCase().includes(input.toLowerCase())}
            options={eligibleCases}
          />

          {/* Melt reconciliation — the vault always loses the full source weight;
              whatever the melt does not yield is a recorded loss, not leftover stock. */}
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              if (!selectedCaseIds.length) return null;
              const type = getFieldValue('batchType');
              let outGross = 0;
              let outNet = 0;
              if (type === 'GROUPED') {
                const { items: gItems, error } = assembleGroupedItems(groupedSource, groupedGroups);
                // Groups still being set up (no purity yet) would read as a total loss.
                if (error) {
                  return (
                    <Alert
                      type="info" showIcon style={{ marginBottom: 16 }}
                      message={`HO vault will be debited the full source weight: ${sourceTotals.gross.toFixed(3)}g gross / ${sourceTotals.net.toFixed(3)}g net`}
                      description="Finish assigning groups and entering each group's purity to see the melting loss."
                    />
                  );
                }
                outGross = gItems.reduce((s, i) => s + (Number(i.grossWeight) || 0), 0);
                outNet = gItems.reduce((s, i) => s + ((Number(i.grossWeight) || 0) * (Number(i.purityPercent) || 0)) / 100, 0);
              } else if (type === 'ITEMIZED') {
                const rows = getFieldValue('items') || [];
                outGross = rows.reduce((s, i) => s + (Number(i?.grossWeight) || 0), 0);
                outNet = rows.reduce((s, i) => s + ((Number(i?.grossWeight) || 0) * (Number(i?.purityPercent) || 0)) / 100, 0);
              } else {
                outGross = Number(getFieldValue('totalGrossWeight')) || 0;
                outNet = (outGross * (Number(getFieldValue('purityPercent')) || 0)) / 100;
              }

              const lossGross = Number((sourceTotals.gross - outGross).toFixed(3));
              const lossNet = Number((sourceTotals.net - outNet).toFixed(3));
              const overweight = lossGross < -0.001;

              return (
                <Alert
                  type={overweight ? 'error' : ((lossGross > 0.001 || lossNet > 0.001) ? 'warning' : 'info')}
                  showIcon
                  style={{ marginBottom: 16 }}
                  message={`HO vault will be debited the full source weight: ${sourceTotals.gross.toFixed(3)}g gross / ${sourceTotals.net.toFixed(3)}g net`}
                  description={overweight
                    ? `This batch claims ${Math.abs(lossGross).toFixed(3)}g more gross than the selected cases hold — a melt cannot produce more metal than went into it. Check the weights.`
                    : `Batch output ${outGross.toFixed(3)}g gross / ${outNet.toFixed(4)}g net. `
                      + `Recorded as melting loss: ${lossGross.toFixed(3)}g gross`
                      + `${lossNet >= 0 ? `, ${lossNet.toFixed(3)}g net` : `, ${Math.abs(lossNet).toFixed(3)}g net gained on re-assay`}. `
                      + 'Nothing is left behind in the vault.'}
                />
              );
            }}
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.batchType !== cur.batchType}>
            {({ getFieldValue }) => {
              if (getFieldValue('batchType') === 'GROUPED') {
                return (
                  <GroupedMeltSection
                    sourceItems={groupedSource}
                    groups={groupedGroups}
                    onChangeSourceItems={setGroupedSource}
                    onChangeGroups={setGroupedGroups}
                  />
                );
              }
              if (getFieldValue('batchType') === 'ITEMIZED') {
                return (
                  <>
                    <Divider orientation="left" plain>Itemized Entries</Divider>
                    <Form.List name="items">
                      {(fields, { add, remove }) => (
                        <>
                          {fields.map((field, idx) => (
                            <Card key={field.key} size="small" style={{ marginBottom: 12, background: '#fafafa' }}>
                              <Row gutter={8} align="middle">
                                <Col span={5}>
                                  <Form.Item label="Label" name={[field.name, 'label']} rules={[{ required: true, message: 'Enter label' }]}>
                                    <Input placeholder={`Item ${idx + 1}`} />
                                  </Form.Item>
                                </Col>
                                <Col span={4}>
                                  <Form.Item label="Gross Wt (g)" name={[field.name, 'grossWeight']} rules={[{ required: true, message: 'Required' }]}>
                                    <InputNumber min={0} precision={3} style={{ width: '100%' }} />
                                  </Form.Item>
                                </Col>
                                <Col span={3}>
                                  <Form.Item label="Purity %" name={[field.name, 'purityPercent']} rules={[{ required: true, message: 'Required' }]}>
                                    <InputNumber min={0} max={100} precision={2} style={{ width: '100%' }} />
                                  </Form.Item>
                                </Col>
                                <Col span={5}>
                                  <Form.Item label="Price (₹)" name={[field.name, 'priceAmount']} rules={[{ required: true, message: 'Required' }]}>
                                    <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                                  </Form.Item>
                                </Col>
                                <Col span={1} style={{ textAlign: 'right' }}>
                                  {fields.length > 1 && (
                                    <Button type="text" icon={<MinusCircleOutlined />} danger onClick={() => remove(field.name)} />
                                  )}
                                </Col>
                              </Row>
                            </Card>
                          ))}
                          <Button type="dashed" block icon={<PlusCircleOutlined />} onClick={() => add({ label: `Item ${fields.length + 1}` })}>
                            Add Item
                          </Button>
                        </>
                      )}
                    </Form.List>
                  </>
                );
              }

              return (
                <>
                  <Divider orientation="left" plain>Single Output Details (Melted Blob)</Divider>
                  <Row gutter={[16, 16]}>
                    <Col span={8}>
                      <Form.Item label="Source Gross Weight (g)" name="sourceTotalGrossWeight">
                        <InputNumber style={{ width: '100%' }} disabled />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item label="Batch Melting Loss (g)" name="meltingLoss" rules={[{ required: true }]}>
                        <InputNumber min={0} precision={3} style={{ width: '100%' }} onChange={(val) => {
                          const srcGross = form.getFieldValue('sourceTotalGrossWeight') || 0;
                          form.setFieldsValue({ totalGrossWeight: Number(Math.max(0, srcGross - (val || 0)).toFixed(3)) });
                        }} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item label="Batch Gross Weight (g)" name="totalGrossWeight" rules={[{ required: true }]}>
                        <InputNumber min={0} precision={3} style={{ width: '100%' }} onChange={(val) => {
                          const srcGross = form.getFieldValue('sourceTotalGrossWeight') || 0;
                          form.setFieldsValue({ meltingLoss: Number(Math.max(0, srcGross - (val || 0)).toFixed(3)) });
                        }} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col span={8}>
                      <Form.Item label="Batch Purity %" name="purityPercent" rules={[{ required: true }]}>
                        <InputNumber min={0} max={100} precision={2} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item noStyle shouldUpdate={(prev, cur) => prev.totalGrossWeight !== cur.totalGrossWeight || prev.purityPercent !== cur.purityPercent}>
                        {({ getFieldValue }) => {
                          const gross = getFieldValue('totalGrossWeight') || 0;
                          const purity = getFieldValue('purityPercent') || 0;
                          const net = ((gross * purity) / 100).toFixed(4);
                          return (
                            <Form.Item label="Batch Net Weight (g)">
                              <InputNumber value={net} style={{ width: '100%' }} disabled />
                            </Form.Item>
                          );
                        }}
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item label="Batch Price (₹)" name="sourceTotalPurchaseCost">
                        <InputNumber style={{ width: '100%' }} disabled />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              );
            }}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
